import { createContext, useCallback, useContext, useRef, useState } from "react";

const ConfirmContext = createContext(null);

/**
 * Hook: const { confirm, prompt } = useConfirm();
 *
 * confirm({ title, message, confirmLabel, cancelLabel, variant })
 *   → Promise<boolean>
 *
 * prompt({ title, message, confirmLabel, cancelLabel, placeholder, initialValue, validate })
 *   → Promise<string|null>   (null = cancelled)
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolveRef = useRef(null);

  // ---- confirm (boolean) ----
  const confirm = useCallback(
    ({ title = "Confirm Action", message = "Are you sure?", confirmLabel = "Confirm", cancelLabel = "Cancel", variant = "normal" } = {}) =>
      new Promise((resolve) => {
        resolveRef.current = resolve;
        setState({ mode: "confirm", title, message, confirmLabel, cancelLabel, variant });
      }),
    [],
  );

  // ---- prompt (string | null) ----
  const prompt = useCallback(
    ({ title = "Input Required", message = "", confirmLabel = "OK", cancelLabel = "Cancel", placeholder = "", initialValue = "", validate } = {}) =>
      new Promise((resolve) => {
        resolveRef.current = resolve;
        setState({ mode: "prompt", title, message, confirmLabel, cancelLabel, placeholder, initialValue, validate });
      }),
    [],
  );

  const handleResolve = (value) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      {state && (
        state.mode === "confirm" ? (
          <ConfirmModal {...state} onConfirm={() => handleResolve(true)} onCancel={() => handleResolve(false)} />
        ) : (
          <PromptModal {...state} onConfirm={(v) => handleResolve(v)} onCancel={() => handleResolve(null)} />
        )
      )}
    </ConfirmContext.Provider>
  );
}

/* ---------- Confirm Modal ---------- */
function ConfirmModal({ title, message, confirmLabel, cancelLabel, variant, onConfirm, onCancel }) {
  const isDanger = variant === "danger";

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-in fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-6 pt-5 pb-3 flex items-start gap-3 ${isDanger ? "" : ""}`}>
          {isDanger ? (
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          ) : (
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 leading-6">{title}</h3>
            <p className="mt-1 text-sm text-gray-600 whitespace-pre-line">{message}</p>
          </div>
        </div>

        {/* Buttons */}
        <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            autoFocus
            className={`px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors ${
              isDanger
                ? "bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                : "bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Prompt Modal ---------- */
function PromptModal({ title, message, confirmLabel, cancelLabel, placeholder, initialValue, validate, onConfirm, onCancel }) {
  const [value, setValue] = useState(initialValue || "");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (validate) {
      const msg = validate(value);
      if (msg) { setError(msg); return; }
    }
    onConfirm(value);
  };

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-in fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-5 pb-3">
          <h3 className="text-lg font-semibold text-gray-900 leading-6">{title}</h3>
          {message && <p className="mt-1 text-sm text-gray-600">{message}</p>}
          <input
            type="text"
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            placeholder={placeholder}
            autoFocus
            className="mt-3 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
        <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
